/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { Spin } from 'antd'
import { useRosterContext } from './context/RosterContext'
import { useUIContext } from './context/UIContext'
import { useScrollToRow } from './hooks/useScrollToRow'
import RosterHeader from './components/RosterHeader'
import PlayerSection from './components/PlayerSection'
import TeamOfficialSection from './components/TeamOfficialSection'

interface UnifiedRosterPageProps {
  teamId: string
  teamName: string
  permission: Record<string, any>
  pendingHighlightId?: string | null
  onPendingHighlightConsumed?: () => void
  onActiveTabChange?: (tab: 'players' | 'officials') => void
  isSoccer?: boolean
  sportName?: string
  sportId?: string
}

const UnifiedRosterPage = ({
  teamId,
  teamName,
  permission,
  pendingHighlightId,
  onPendingHighlightConsumed,
  onActiveTabChange,
  isSoccer,
  sportName,
  sportId,
}: UnifiedRosterPageProps) => {
  const {
    loading,
    refreshingRosterData,
    loadingRosters,
    rosters,
    error,
    teamOfficials,
    players,
    refresh,
  } = useRosterContext()
  const { addHighlightedRow } = useUIContext()
  const scrollToRow = useScrollToRow()
  const [activeTab, setActiveTabRaw] = useState<'players' | 'officials'>(
    'players'
  )
  const setActiveTab = (tab: 'players' | 'officials') => {
    setActiveTabRaw(tab)
    onActiveTabChange?.(tab)
  }

  const canEdit = permission?.update || permission?.admin
  const hasPlayers = players.length > 0
  const hasOfficials = teamOfficials.length > 0

  // For non-editors: determine which tabs to show based on data availability
  const getVisibleTabs = (): readonly ('players' | 'officials')[] => {
    if (canEdit) {
      return ['players', 'officials']
    }
    const tabs: ('players' | 'officials')[] = []
    if (hasPlayers) {
      tabs.push('players')
    }
    if (hasOfficials) {
      tabs.push('officials')
    }
    return tabs
  }
  const visibleTabs = getVisibleTabs()

  const showTabSwitcher = visibleTabs.length > 1

  // Auto-correct activeTab if it's not in the visible set (e.g. no-permission user
  // with only officials data defaults to 'players' but that tab isn't visible)
  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.includes(activeTab as 'players' | 'officials')
    ) {
      setActiveTab(visibleTabs[0])
    }
  }, [visibleTabs, activeTab])

  // Highlight and scroll to a row added from outside the provider
  useEffect(() => {
    if (!pendingHighlightId) {
      return
    }
    // Wait until the new official appears in the data
    const found = teamOfficials.some((o: any) => o.id === pendingHighlightId)
    if (found) {
      setActiveTab('officials')
      addHighlightedRow(pendingHighlightId, 'success')
      scrollToRow(pendingHighlightId)
      onPendingHighlightConsumed?.()
    }
  }, [
    teamOfficials,
    pendingHighlightId,
    addHighlightedRow,
    scrollToRow,
    onPendingHighlightConsumed,
  ])

  if (loadingRosters) {
    return (
      <div
        css={css`
          margin-top: 24px;
          align-self: center;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  return (
    <div
      css={css`
        margin-top: 24px;
        margin-bottom: 100px;
      `}
    >
      <RosterHeader
        teamId={teamId}
        teamName={teamName}
        permission={permission}
      />
      {error ? (
        <div
          css={css`
            margin: 0 40px;
            word-break: break-all;
            text-align: center;
          `}
        >
          <p
            css={css`
              font-weight: 700;
              font-size: 20px;
              text-align: center;
              margin-bottom: 16px;
            `}
          >
            Error loading roster data
          </p>
          {error}
        </div>
      ) : loading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 24px;
          `}
        >
          <Spin size='small' />
        </div>
      ) : rosters.length > 0 && visibleTabs.length > 0 ? (
        <>
          {showTabSwitcher && (
            <div
              css={css`
                display: inline-flex;
                align-items: center;
                gap: 2px;
                background-color: transparent;
                border: 1px solid ${colors.SECONDARY_200};
                border-radius: 8px;
                padding: 4px;
                height: 40px;
              `}
            >
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab
                const label = tab === 'players' ? 'Players' : 'Team Officials'
                return (
                  <button
                    key={tab}
                    type='button'
                    onClick={() => setActiveTab(tab)}
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      min-width: 98px;
                      padding: 0 12px;
                      height: 32px;
                      border-radius: 6px;
                      background-color: ${isActive
                        ? colors.SECONDARY_300
                        : 'transparent'};
                      border: none;
                      cursor: pointer;
                      transition: all 0.2s ease;
                      font-size: 14px;
                      font-weight: ${isActive ? 700 : 400};
                      line-height: 20.3px;
                      color: ${colors.NEUTRAL_0};
                      white-space: nowrap;

                      &:hover {
                        background-color: ${isActive
                          ? colors.SECONDARY_300
                          : colors.SECONDARY_200};
                      }

                      &:active {
                        transform: scale(0.98);
                      }
                    `}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          )}
          {/* SectionTitle inside each section has margin-top: 24px.
             When the tab switcher is visible we collapse that to 16px total gap. */}
          <div
            css={
              showTabSwitcher
                ? css`
                    margin-top: -8px;
                  `
                : undefined
            }
          >
            {activeTab === 'players' && visibleTabs.includes('players') ? (
              <PlayerSection
                onRefresh={refresh}
                loading={refreshingRosterData}
                key='players'
                teamId={teamId}
                permission={permission}
                isSoccer={isSoccer}
                sportName={sportName}
                sportId={sportId}
              />
            ) : visibleTabs.includes('officials') ? (
              <TeamOfficialSection
                onRefresh={refresh}
                loading={refreshingRosterData}
                key='officials'
                teamId={teamId}
                permission={permission}
                sportId={sportId}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}

export default UnifiedRosterPage
